/**
 * Shared axios instances for the app's two backends: `api` for the main REST
 * API (/api/v1, cookie-based Flask sessions) and `aiApi` for the AI service
 * (/ai/v1). Base URLs switch between localhost and relative paths by env.
 * Response interceptors normalize errors via processApiError/processAiApiError;
 * on a 401 from `api`, the user context is cleared and the app navigates to
 * /signUpLogin using handlers registered through setupAuthHandlers.
 * Request interceptors log every outgoing request config.
 * All modules in app/api/ build on these instances.
 */
import { processAiApiError, processApiError } from "@/utils/api";
import axios, { InternalAxiosRequestConfig } from "axios";
import { clearUserContext, UserAction } from "@/contexts/UserContext";
import { NavigateFunction } from "react-router-dom";
import { getEnv } from "@/utils/config";

let userDispatch: React.Dispatch<UserAction> | null = null;
let navigate: NavigateFunction | null = null;

export const setupAuthHandlers = (dispatch: React.Dispatch<UserAction>, nav: NavigateFunction) => {
    userDispatch = dispatch;
    navigate = nav;
};

function getApiBaseUrl() {
    try {
        const env = getEnv();
        if (env === "prod") {
            return "/api/v1";
        } else if (env === "dev") {
            return "http://localhost:8000/api/v1";
        }
        console.error("Unknown environment:", env);
        return "/api/v1";
    } catch (error) {
        console.error("Error getting environment:", error);
        return "/api/v1";
    }
}

function getAiBaseUrl() {
    try {
        const env = getEnv();
        if (env === "prod") {
            return "/ai/v1";
        } else if (env === "dev") {
            return "http://localhost:8001/ai/v1";
        }
        console.error("Unknown environment:", env);
        return "/ai/v1";
    } catch (error) {
        console.error("Error getting environment:", error);
        return "/ai/v1";
    }
}

export const api = axios.create({
    baseURL: getApiBaseUrl(),
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true, // Required for Flask sessions cookies
});

export const aiApi = axios.create({
    baseURL: getAiBaseUrl(),
    headers: {
        "Content-Type": "application/json",
    },
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            if (userDispatch && navigate) {
                clearUserContext(userDispatch);
                navigate("/signUpLogin");
            }
            return Promise.reject("Unauthorized");
        }
        // Puts a string error message in the reason
        return Promise.reject(processApiError(error));
    }
);

api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const log: Record<string, unknown> = {
            fullUrl: (config.baseURL || "") + (config.url || ""),
            method: config.method,
            headers: config.headers,
            env: getEnv(),
            windowLocation: window.location.toString(),
            runtimeConfig: window.RUNTIME_CONFIG,
        };
        if (config.params !== undefined) log.params = config.params;
        if (config.data !== undefined) log.payload = config.data;
        console.log("Request Config:", log);
        return config;
    },
    (error) => {
        console.error("Request Error:", error);
        return Promise.reject(error);
    }
);

aiApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        return Promise.reject(processAiApiError(error));
    }
);

aiApi.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const log: Record<string, unknown> = {
            fullUrl: (config.baseURL || "") + (config.url || ""),
            method: config.method,
            headers: config.headers,
            env: getEnv(),
            windowLocation: window.location.toString(),
            runtimeConfig: window.RUNTIME_CONFIG,
        };
        if (config.params !== undefined) log.params = config.params;
        if (config.data !== undefined) log.payload = config.data;
        console.log("Request Config:", log);
        return config;
    },
    (error) => {
        console.error("Request Error:", error);
        return Promise.reject(error);
    }
);
